import { find } from 'lodash'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import sideBarOptions from './sideBarOptions'
import ExpandedSideBar from '@/components/sideBar/ExpandedSideBar'
import CompactSideBar from '@/components/sideBar/CompactSideBar'
import { findAllNodesByPropertyValue, findNodeByPropertyValue } from '@/components/sideBar/sideBarUtils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { SampleOperation } from '@/api/operations/models/sample-operation'

class GrossingSideBarStore {
    constructor() {
        this.sideBars = [ ExpandedSideBar, CompactSideBar ]
        this.loadedCustomOptions = false
        this.loadedConfig = false
        this.defaultOption = findNodeByPropertyValue('id', 'forGrossing', sideBarOptions.expanded)
        this.state = {
            optionsReady: false,
            externalFilterModel: {},
            componentCode: 'uicomponent.grossing.samplepanel',
            options: sideBarOptions,
            sideBar: ExpandedSideBar,
            fallbackOption: this.defaultOption,
            selectedOption: this.defaultOption,
            sideBarName: sideBarOptions.sideBarName,
            gridRouteName: 'grossingGrid',
            customOptions: [],
            config: this.getDefaultConfig()
        }
    }
    callWhenOptionsReady(callback) {
        const checkOptionsReady = setInterval(() => {
            if (this.state.optionsReady) {
                callback()
                clearInterval(checkOptionsReady)
            }
        }, 300)
    }
    getDefaultConfig() {
        return {
            defaultOptionId: null,
            sideBarName: ExpandedSideBar.name,
            viewAllEnabled: false
        }
    }
    hasLoaded() {
        return this.loadedCustomOptions && this.loadedConfig
    }
    async expandSideBar() {
        this.state.sideBar = ExpandedSideBar
        await this.saveConfig()
    }
    async contractSideBar() {
        this.state.sideBar = CompactSideBar
        await this.saveConfig()
    }
    contractSideBarLocally() {
        this.state.sideBar = CompactSideBar
    }
    selectDefaultOption() {
        const option = this.findOptionById(this.state.config.defaultOptionId)

        if (!option) {
            console.warn(`customOption with id ${this.state.config.defaultOptionId} does not exist, selecting fallback option`)
            this.selectFallbackOption()
            return
        }
        this.setSelectedOption(option)
    }
    selectFallbackOption() {
        this.setSelectedOption(this.state.fallbackOption)
    }
    findOptionById(id) {
        return findNodeByPropertyValue('id', id, sideBarOptions.expanded) || find(this.state.customOptions, o => o.id === id)
    }
    setSelectedOption(option) {
        this.state.selectedOption = option
    }
    async getSelectedOption() {
        return this.state && this.state.selectedOption
    }
    setDefaultOption(option) {
        this.state.config.defaultOptionId = option && option.id
    }
    setViewAll(viewAllEnabled) {
        this.state.config.viewAllEnabled = viewAllEnabled
    }
    async loadCustomOptions() {
        const customOptions = await customFilterResource.indexAsOptions(this.state.componentCode)
        this.setCustomOptions(customOptions)
        this.loadedCustomOptions = true
    }
    setCustomOptions(customOptions) {
        this.state.customOptions = customOptions
    }
    async removeCustomOption(option) {
        const index = this.state.customOptions.findIndex(i => i.id === option.id)
        if (index === -1) {
            throw new Error(`custom option with id ${option.id} does not exist`)
        }
        await customFilterResource.deleteCustomOption(this.state.componentCode, option)
        if (option.id === this.state.selectedOption.id) {
            this.setSelectedOption(this.defaultOption)
        }
        this.state.customOptions.splice(index, 1)
    }
    async renameCustomOption(option) {
        await customFilterResource.renameCustomOption(this.state.componentCode, option)
        await this.loadCustomOptions()
        this.updateSelectedOption()
    }
    async updateCustomOption(option, filterModel) {
        return customFilterResource.updateCustomOption(this.state.componentCode, option, filterModel)
    }
    updateSelectedOption() {
        const customOptionSelectedOption = this.state.customOptions.find(customOption => customOption.id === this.state.selectedOption?.id)
        if (customOptionSelectedOption) {
            this.setSelectedOption(customOptionSelectedOption)
        }
    }
    _viewAllAllowed() {
        return HelperUtils.hasOperationInCurrentLaboratory(SampleOperation.VIEW_ALL_PROCESS)
    }
    async loadConfig() {
        const customConfig = await customConfigResource.get(this.state.componentCode)
        if (customConfig) {
            const config = {
                ...customConfig.config,
                viewAllEnabled: this._viewAllAllowed() ? customConfig.config.viewAllEnabled : false
            }
            this.setConfig({ ...this.getDefaultConfig(), ...config })
        } else {
            this.setConfig(this.getDefaultConfig())
        }
        this.loadedConfig = true
    }
    setConfig(config) {
        this.state.config = config
        this.applyConfigSideBar()
    }
    applyConfigSideBar() {
        const sideBar = this.sideBars.find(i => i.name === this.state.config.sideBarName)
        if (!sideBar) {
            throw new Error(`sideBar named ${this.state.config.sideBarName} not found`)
        }
        this.state.sideBar = sideBar
    }
    async saveConfig() {
        const config = {
            ...this.state.config,
            sideBarName: this.getCurrentSideBarName()
        }
        await customConfigResource.save(this.state.componentCode, config)
        this.state.config = config
    }
    getCurrentSideBarName() {
        if (!this.state.sideBar) {
            throw new Error('state.sideBar is not set')
        }
        return this.state.sideBar.name
    }
    getSummaryRequest() {
        const options = findAllNodesByPropertyValue('summary', true, this.state.options.expanded)
        const summaryRequest = { filters: {} }
        options.forEach(option => {
            summaryRequest.filters[option.id] = {
                grossingTask: option.task.code,
                filterModel: option.filterModel || {}
            }
        })
        return summaryRequest
    }
}
export default new GrossingSideBarStore()
